// The path a thrown grenade takes, against the real map.
//
// Used twice on the client: for the ghost grenade drawn between your throw and the server's reply,
// and for the aiming arc. Both have to agree with each other and be close to the server's Rapier
// body, or the preview teaches the player something the game will not honour.
//
// This is a swept sphere approximation, not a rigid body. It matches the server exactly in free
// flight, because it applies the same gravity and the same linear damping in the same order, and it
// sweeps a ball of the real radius so a grazing pass clips what the server's ball would clip.
//
// It is not a rigid body solver: no spin, no angular damping, no contact solver. So a bounce is
// close rather than exact, and a long roll along a surface will drift. That is why the aiming arc
// stops at the first bounce, which is the last point it can honestly promise.
import { sweepBall } from "./physics.js";
import { GRAVITY, KINDS, Kind, STEP_DT, GRENADE_LINEAR_DAMPING } from "./tuning.js";
const MAX_BOUNCES_PER_STEP = 4;
/**
 * The engine advances a body over this many gravity substeps within one step, then damps the
 * velocity once. Measured against a real body rather than assumed: with any other value the drawn
 * path drifts from the server by centimetres per second. A test pins it.
 */
const GRAVITY_SUBSTEPS = 4;
/**
 * Advance one grenade by one step. Returns true if it hit something on the way.
 * With no physics environment it falls back to a plain arc, which is only used before the map loads.
 */
export function stepBallistic(env, g, dt, kind) {
    const def = KINDS[kind] ?? KINDS[Kind.Frag];
    const r = def.radius;
    // Displacement over the step, with the same substepped gravity the engine applies, then the
    // velocity carried into the next step: full gravity, then damping, exactly once.
    const gcorr = (GRAVITY * dt * dt * (GRAVITY_SUBSTEPS + 1)) / (2 * GRAVITY_SUBSTEPS);
    let dx = g.vx * dt, dy = g.vy * dt - gcorr, dz = g.vz * dt;
    g.vy -= GRAVITY * dt;
    const damp = 1 / (1 + GRENADE_LINEAR_DAMPING * dt);
    g.vx *= damp;
    g.vy *= damp;
    g.vz *= damp;
    let bounced = false;
    for (let i = 0; i < MAX_BOUNCES_PER_STEP; i++) {
        const dist = Math.hypot(dx, dy, dz);
        if (dist < 1e-9)
            break;
        const ux = dx / dist, uy = dy / dist, uz = dz / dist;
        const hit = env ? sweepBall(env, g.x, g.y, g.z, ux, uy, uz, dist, r) : null;
        if (!hit) {
            g.x += dx;
            g.y += dy;
            g.z += dz;
            break;
        }
        // The cast already stops where the ball's surface meets the map, so this is the whole distance.
        const travel = Math.max(0, hit.toi);
        g.x += ux * travel;
        g.y += uy * travel;
        g.z += uz * travel;
        // Split the velocity about the surface normal: the normal part bounces and loses energy to
        // restitution, the tangent part is scrubbed by friction.
        const nx = hit.nx, ny = hit.ny, nz = hit.nz;
        const vn = g.vx * nx + g.vy * ny + g.vz * nz;
        const tx = g.vx - vn * nx, ty = g.vy - vn * ny, tz = g.vz - vn * nz;
        const keep = Math.max(0, 1 - def.friction * 0.5);
        const bounce = -vn * def.restitution;
        g.vx = tx * keep + nx * bounce;
        g.vy = ty * keep + ny * bounce;
        g.vz = tz * keep + nz * bounce;
        bounced = true;
        // Carry the rest of the step along the new heading.
        const speed = Math.hypot(g.vx, g.vy, g.vz);
        if (speed < 0.4)
            break;
        const remain = Math.max(0, dist - travel);
        if (remain < 1e-9)
            break;
        dx = (g.vx / speed) * remain;
        dy = (g.vy / speed) * remain;
        dz = (g.vz / speed) * remain;
    }
    return bounced;
}
/**
 * The path from a throw to its first bounce, for the aiming arc. Stops at the first contact
 * because past that point the prediction is guesswork and showing it would be a lie.
 */
export function arcToFirstBounce(env, x, y, z, vx, vy, vz, kind, maxSteps = 100) {
    const g = { x, y, z, vx, vy, vz };
    const points = [{ ...g }];
    for (let i = 0; i < maxSteps; i++) {
        const bounced = stepBallistic(env, g, STEP_DT, kind);
        points.push({ ...g });
        if (bounced)
            break;
    }
    return points;
}
//# sourceMappingURL=arc.js.map